import { useApp } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import {
  Badge,
  Card,
  RcDl,
  RcLink,
  ShowDetailsSection,
  ShowStatus,
} from '../../../shared';

function ProductionOrdersShowDetails({ productionOrder }) {
  const { isAdmin } = useApp();

  function formatState(state, prefix) {
    if (!state) {
      return '';
    }

    return state
      .replace(`${prefix}_`, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function getBaseUrl() {
    return import.meta.env.VITE_APP_ENV === 'production'
      ? 'https://legacy.retainerclub.com'
      : 'https://rc-staging.net';
  }

  return (
    <Card className="h-full p-4" heading="Details" wrapperClassName="h-full">
      <div className="mt-4" />
      <div className="flex flex-col gap-x-8 gap-y-4 sm:flex-row">
        <div className="flex-1">
          <RcDl dividers={false} gridCols="sm:grid-cols-2 gap-y-4 gap-x-8">
            <ShowDetailsSection title="Ship To Clinic" horizontal={false}>
              <div className="mt-2">
                <Badge
                  withDot={false}
                  name={productionOrder.sendToClinic ? 'Yes' : 'No'}
                  color={productionOrder.sendToClinic ? 'green' : 'yellow'}
                />
              </div>
            </ShowDetailsSection>

            <ShowDetailsSection
              title={isAdmin ? 'Statuses' : 'Status'}
              horizontal={false}
            >
              <div className="mt-2">
                {isAdmin && (
                  <>
                    <div>
                      <span className="font-semibold text-rcprimary-800/80">
                        Admin:
                      </span>
                      &nbsp;
                      <span className="italic">
                        {formatState(productionOrder.state, 'admin')}
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className="font-semibold text-rcprimary-800/80">
                        Customer:
                      </span>
                      &nbsp;
                      <span className="italic">
                        {formatState(productionOrder.customerState, 'customer')}
                      </span>
                    </div>
                  </>
                )}

                <div className="mt-1">
                  {isAdmin && (
                    <>
                      <span className="font-semibold text-rcprimary-800/80">
                        Partner:
                      </span>
                      &nbsp;
                    </>
                  )}

                  <span className="italic">
                    {formatState(productionOrder.partnerState, 'partner')}
                  </span>
                </div>
                {isAdmin && (
                  <div className="mt-1">
                    <span className="font-semibold text-rcprimary-800/80">
                      Manufacturer:
                    </span>
                    &nbsp;
                    <span className="italic">
                      {formatState(
                        productionOrder.manufacturerState,
                        'manufacturer',
                      )}
                    </span>
                  </div>
                )}
              </div>
            </ShowDetailsSection>
            {isAdmin && (
              <ShowDetailsSection title="Production Notes" horizontal={false}>
                <div className="mt-2">
                  {productionOrder.productionNotes || 'None'}
                </div>
              </ShowDetailsSection>
            )}
          </RcDl>
        </div>
        <div className="flex-1">
          <RcDl dividers={false} gridCols="sm:grid-cols-2 gap-y-4 gap-x-8">
            {isAdmin && (
              <ShowDetailsSection
                title="Sent To External Manufacturer"
                horizontal={false}
              >
                <div className="mt-2">
                  <Badge
                    withDot={false}
                    name={
                      productionOrder.sentToExternalManufacturer ? 'Yes' : 'No'
                    }
                    color={
                      productionOrder.sentToExternalManufacturer
                        ? 'green'
                        : 'yellow'
                    }
                  />
                </div>
              </ShowDetailsSection>
            )}

            <ShowDetailsSection title="Arches" horizontal={false}>
              <div className="mt-2 capitalize">
                <span className="font-semibold text-rcprimary-800/80">
                  Treating:
                </span>
                &nbsp;
                <span className="italic">{productionOrder.arches}</span>
              </div>
              <div className="mt-1">
                <span className="font-semibold text-rcprimary-800/80">
                  Upper:
                </span>
                &nbsp;
                <span className="italic">
                  {productionOrder.upperArchQuantity}
                </span>
              </div>
              <div className="mt-1">
                <span className="font-semibold text-rcprimary-800/80">
                  Lower:
                </span>
                &nbsp;
                <span className="italic">
                  {productionOrder.lowerArchQuantity}
                </span>
              </div>
            </ShowDetailsSection>
            {isAdmin && (
              <ShowDetailsSection
                title="PFG Case(s) Submitted"
                horizontal={false}
              >
                <div className="mt-2">
                  {productionOrder.warranties?.length > 0 ? (
                    <div>
                      {productionOrder.warranties.map((warranty) => (
                        <div key={warranty.id}>
                          <RcLink
                            to={`/perfect-fit-guarantees/${warranty.id}`}
                            className="font-bold uppercase"
                          >
                            {warranty.hexCode}
                          </RcLink>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'None'
                  )}
                </div>
              </ShowDetailsSection>
            )}

            {isAdmin && productionOrder.warrantyId && (
              <ShowDetailsSection title="Created From PFG" horizontal={false}>
                <div className="mt-2">
                  <RcLink
                    to={`/perfect-fit-guarantees/${productionOrder.warrantyId}`}
                    className="font-bold uppercase"
                  >
                    {productionOrder.warranty.hexCode}
                  </RcLink>
                </div>
              </ShowDetailsSection>
            )}

            {productionOrder.legacyId && isAdmin && (
              <ShowDetailsSection title="Legacy Import" horizontal={false}>
                <div>
                  <div>Shipment {productionOrder.legacyId}</div>
                  {productionOrder.importOrderType && (
                    <div className="mt-2">
                      <span className="mr-2 font-medium text-gray-700 text-sm">
                        Order Type:
                      </span>
                      <ShowStatus state={productionOrder.importOrderType} />
                    </div>
                  )}
                </div>
              </ShowDetailsSection>
            )}

            {!productionOrder.legacyId && productionOrder.order && isAdmin && (
              <ShowDetailsSection title="Order" horizontal={false}>
                <div>
                  <RcLink
                    to={`/customers/orders/${productionOrder.order.id}`}
                    className="font-bold text-rcprimary-600 uppercase hover:text-rcprimary-800 hover:underline"
                  >
                    {productionOrder.order.hexCode}
                  </RcLink>
                </div>
              </ShowDetailsSection>
            )}
          </RcDl>
        </div>
      </div>
      {productionOrder.legacyId && (
        <div className="mt-4 flex justify-end">
          <a
            href={`${getBaseUrl()}/${productionOrder.legacyId}/production-label-print`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-rcprimary-600 text-sm hover:text-rcprimary-800 hover:underline"
          >
            Click for 1.0 Production slip
          </a>
        </div>
      )}
    </Card>
  );
}

ProductionOrdersShowDetails.propTypes = {
  productionOrder: PropTypes.object.isRequired,
};

export default ProductionOrdersShowDetails;
